import React from "react";
import { importChunkWithRetry } from "@/lib/lazyWithRetry";

/**
 * U35 / FI-5219: the toast focus pause and pointer-exit focus guard. The installer lives in the LAZY
 * `toastFocusPauseInstaller.ts` (r184 round-1 landing: FI-5219 put `main_bundle.js` 141 B over its budget); mechanism
 * and history are on that module.
 *
 * <p>Loaded with the retry-only helper: the pause is an enhancement no member waits on (sonner still pauses on hover and
 * press), so a failed chunk degrades quietly and never reloads the page. A spec passes `install` to mount it
 * synchronously; production passes nothing.
 */
export type ToastFocusPauseInstaller = (doc: Document) => () => void;

/** Mounted once, by `ToasterReactComponent`. */
export function useToastFocusPause(install?: ToastFocusPauseInstaller): void {
    React.useEffect(() => {
        if (install) {
            return install(document);
        }
        let uninstall: (() => void) | null = null;
        let unmounted = false;
        importChunkWithRetry(() => import("@/main/react/header/toastFocusPauseInstaller"), "toast-focus-pause")
            .then((mod) => {
                if (!unmounted) {
                    uninstall = mod.default(document);
                }
            })
            .catch(() => {
                // Enhancement only: without it a toast still pauses on hover and press.
            });
        return () => {
            unmounted = true;
            uninstall?.();
        };
    }, [install]);
}
